import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { between, only, up } from 'styled-breakpoints';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import Button, {
  SecondaryButton,
} from '@rebound/design-system/components/Button';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import { DraftReturnItemAction } from '@rebound/shared-models/draft-return/draft-return.model';
import { ThemeColorScheme } from '@rebound/shared-models/retailer/retailer.model';
import {
  Return,
  TrackingHistoryLocation,
} from '@rebound/shared-models/return/return.model';

import { HighlightedWrapper } from '@components/HighlightedWrapper';
import ImagePlaceholder, {
  ItemImageVariant,
} from '@components/ImagePlaceholder';
import Picture from '@components/Picture';
import {
  GetDraftReturnByRoIdQuery,
  GetDraftReturnByRoIdQueryResult,
} from '@modules/draft-return/draft-return.queries';

import { currencyFormatter, ReturnStatusCodeTexts } from '../dashboard.utils';
import ReturnLabelButton from './ReturnLabelButton';

const getLocationInfo = (location: TrackingHistoryLocation) => `${
  location.city ? `${location.city}` : ''
}
${location.state ? `${location.state}, ` : ''}
${location.country || ''}`;

type ReturnCardProps = {
  returnObject: Return;
  scheme?: string;
  trackReturnPreview?: boolean;
  highlighted?: boolean | null;
};

const ReturnCard = ({
  returnObject,
  scheme = ThemeColorScheme.Indigo,
  trackReturnPreview = false,
  highlighted = null,
}: ReturnCardProps) => {
  const { t } = useTranslation();
  const [historyOpened, setHistoryOpened] = useState(trackReturnPreview);

  const { data: draftReturn } = useQuery<GetDraftReturnByRoIdQueryResult>(
    GetDraftReturnByRoIdQuery,
    {
      variables: {
        returnOrderId: returnObject.id,
      },
    },
  );

  const returnItemsWithKeys = useMemo(
    () =>
      returnObject.items.map((item) => ({
        ...item,
        key: uuid(),
      })),
    [returnObject],
  );

  const returnTrackingHistoryWithKeys = useMemo(
    () =>
      returnObject.trackingHistory
        .map((item) => ({ ...item, key: uuid() }))
        .sort((a, b) => b.date - a.date),
    [returnObject.trackingHistory],
  );

  const getHistoryItemsToShow = useCallback(
    (maxItemsToShow: number) =>
      historyOpened || returnTrackingHistoryWithKeys.length <= maxItemsToShow
        ? returnTrackingHistoryWithKeys.slice(
            0,
            returnTrackingHistoryWithKeys.length,
          )
        : returnTrackingHistoryWithKeys.slice(0, 1),
    [returnTrackingHistoryWithKeys, historyOpened],
  );

  const historyItemsToShow = useMemo(
    () => getHistoryItemsToShow(2),
    [getHistoryItemsToShow],
  );

  const hasTrackingHistoryShowMore = returnTrackingHistoryWithKeys.length >= 3;

  const historyItemsToShowMobile = useMemo(
    () => getHistoryItemsToShow(1),
    [getHistoryItemsToShow],
  );

  const hasTrackingHistoryShowMoreMobile =
    returnTrackingHistoryWithKeys.length > 1;

  const storeCreditItems = returnObject.items.filter(
    (item) => item.returnType === DraftReturnItemAction.ExchangeToCredits,
  );
  const exchangeItems = returnObject.items.filter(
    (item) => item.returnType === DraftReturnItemAction.ExchangeToItem,
  );

  const totalRefund = returnObject.refundValue;
  const totalStoreCredit = storeCreditItems.reduce(
    (sum, item) => sum + (item.storeCreditRefund?.amount || 0),
    0,
  );
  const currencyCode = returnObject.currencyCode;

  const hasRefund = totalRefund != null && totalRefund > 0;
  const hasStoreCredit = totalStoreCredit > 0;
  const hasExchangeItems = exchangeItems.length > 0;

  const hasSummary = hasRefund || hasStoreCredit || hasExchangeItems;

  return (
    <Wrapper
      id={returnObject.id}
      data-cy="return-card-item"
      scheme={scheme}
      highlighted={highlighted}
    >
      <OrderReference data-cy="return-card-tracking-number">
        <>
          {t('returnsListing.orderReferenceLabel')} #
          {returnObject.orderReference}
          <CurrentTrackingStatus>
            {`${t('returnsListing.currentReturnStatusLabel')} ${t(
              ReturnStatusCodeTexts[returnObject.currentTrackingStatus],
            )}`}
          </CurrentTrackingStatus>
        </>
      </OrderReference>

      <ReturnInfoWrapper>
        <ReturnInfo>
          <ReturnInfoTitle>
            {t('ordersListing.returnDeliveryMethodLabel') as string}
          </ReturnInfoTitle>
          <ReturnCarrier>
            {returnObject.carrierName || returnObject.carrier}
          </ReturnCarrier>
        </ReturnInfo>
        {draftReturn?.getDraftReturnByRoId.dropOffPointAddress ? (
          <ReturnInfo>
            <ReturnInfoTitle>
              {t('ordersListing.dropOffLocationLabel') as string}
            </ReturnInfoTitle>
            <p>{draftReturn.getDraftReturnByRoId.dropOffPointAddress}</p>
          </ReturnInfo>
        ) : null}
        {hasSummary && (
          <ReturnInfo featured>
            <ReturnInfoTitle>
              {t('ordersListing.returnSummaryLabel') as string}
            </ReturnInfoTitle>

            {hasRefund && (
              <p>
                {currencyFormatter(currencyCode).format(totalRefund)}&nbsp;
                {t('returnsListing.returnSummaryRefund') as string}
              </p>
            )}

            {hasStoreCredit && (
              <p>
                {currencyFormatter(currencyCode).format(totalStoreCredit)}&nbsp;
                {t('returnsListing.returnSummaryStoreCreditRefund') as string}
              </p>
            )}

            {hasExchangeItems && (
              <p>
                {t('returnsListing.returnSummaryExchangeItems', {
                  count: exchangeItems.length,
                })}
              </p>
            )}
          </ReturnInfo>
        )}
      </ReturnInfoWrapper>
      <ReturnItemsTitle>
        {t('returnsListing.orderItemsSubtitle') as string}
      </ReturnItemsTitle>
      <ReturnItemsWrapper>
        {returnItemsWithKeys.map((item) => (
          <ReturnItem key={item.key}>
            <ReturnItemImageWrapper>
              {item.imgPath ? (
                <Picture src={item.imgPath} alt={item.skuDesc} loading="lazy" />
              ) : (
                <PlaceHolderWrapper>
                  <ImagePlaceholder variant={ItemImageVariant.Hanger} />
                  <PlaceholderInfo>
                    {t('ordersListing.noItemImageLabel') as string}
                  </PlaceholderInfo>
                </PlaceHolderWrapper>
              )}
            </ReturnItemImageWrapper>
            <ItemDescription>{item.skuDesc}</ItemDescription>
            <ItemInfo>
              {item.reason[0].reason ||
                (t('returnsListing.noReasonLabel') as string)}
            </ItemInfo>
          </ReturnItem>
        ))}
      </ReturnItemsWrapper>

      <ReturnTrackingHistoryWrapper
        bordered={!hasTrackingHistoryShowMore || historyOpened}
      >
        <TrackingRow featured bordered>
          <StatusColumnTitle>
            {t('returnsListing.trackingHistoryStatusTitle') as string}
          </StatusColumnTitle>
          <LocationColumnTitle>
            {t('returnsListing.trackingHistoryLocationTitle') as string}
          </LocationColumnTitle>
          <DateColumnTitle>
            {t('returnsListing.trackingHistoryDateTitle') as string}
          </DateColumnTitle>
        </TrackingRow>
        {historyItemsToShow.map((historyItem, i) => (
          <TrackingRow featured={i % 2 !== 0} key={historyItem.key}>
            <StatusColumn>
              {t(ReturnStatusCodeTexts[historyItem.status]) as string}
            </StatusColumn>
            <LocationColumn>
              {getLocationInfo(historyItem.location)}
            </LocationColumn>
            <DateColumn>
              {format(historyItem.date, 'dd MMMM yyyy, HH:mm')}
            </DateColumn>
          </TrackingRow>
        ))}
      </ReturnTrackingHistoryWrapper>

      <ReturnTrackingHistoryWrapperMobile>
        <TrackingHistoryTitle>
          {t('returnsListing.trackingHistoryTitle') as string}
        </TrackingHistoryTitle>
        <TrackingHistorySubtitleMobile scheme={scheme}>
          {t('returnsListing.trackingHistorySubtitleMobile') as string}
        </TrackingHistorySubtitleMobile>
        {
          <TrackingHistoryListMobile>
            {historyItemsToShowMobile.map((historyItem) => (
              <div key={historyItem.key}>
                <TrackingHistoryItemStatusMobile>
                  {t(ReturnStatusCodeTexts[historyItem.status]) as string}
                </TrackingHistoryItemStatusMobile>
                <TrackingHistoryItemContentMobile scheme={scheme}>
                  {historyItem.location.city ||
                  historyItem.location.country ||
                  historyItem.location.state ? (
                    <TrackingHistoryItemLocationMobile>
                      {getLocationInfo(historyItem.location)}
                    </TrackingHistoryItemLocationMobile>
                  ) : null}
                  <TrackingHistoryItemDateMobile>
                    {format(historyItem.date, 'dd MMMM yyyy, HH:mm')}
                  </TrackingHistoryItemDateMobile>
                </TrackingHistoryItemContentMobile>
              </div>
            ))}
          </TrackingHistoryListMobile>
        }
      </ReturnTrackingHistoryWrapperMobile>

      {!trackReturnPreview && !historyOpened ? (
        <ShowFullHistoryButtonWrapper
          isVisibleDesktop={hasTrackingHistoryShowMore}
          isVisibleMobile={hasTrackingHistoryShowMoreMobile}
        >
          <ShowFullHistoryButton
            scheme={scheme}
            onClick={() => setHistoryOpened(true)}
          >
            {t('returnsListing.showFullHistoryLabel') as string}
          </ShowFullHistoryButton>
        </ShowFullHistoryButtonWrapper>
      ) : null}
      <ReturnLabelButtonWrapper>
        {!trackReturnPreview && (
          <ButtonLink
            href={`/dashboard/orders?highlightOrder=${returnObject.orderReference}`}
            passHref
          >
            <ViewOrderButton
              scheme={scheme}
              title={t('ordersListing.viewOrder')}
            />
          </ButtonLink>
        )}
        <ReturnLabelButton
          returnObject={{
            ...returnObject,
            carrierName: returnObject.carrierName ?? '',
          }}
          scheme={scheme}
        />
      </ReturnLabelButtonWrapper>
    </Wrapper>
  );
};

const ReturnCarrier = styled.div`
  max-width: 150px;
`;

const Wrapper = styled(HighlightedWrapper)<{
  scheme: string;
  highlighted: boolean | null;
}>`
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: ${(props) => `1px solid ${props.theme.colors.grayLight3}`};
  padding-top: 20px;
  margin-bottom: 20px;
  position: relative;
  box-shadow:
    0 10px 20px rgba(0, 0, 0, 0.04),
    0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  transition:
    transform 2s cubic-bezier(0.165, 0.84, 0.44, 1),
    box-shadow 2s cubic-bezier(0.165, 0.84, 0.44, 1),
    border-color 3s;
`;

const OrderReference = styled.div`
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;
  padding-left: 16px;
  margin-bottom: 22px;

  ${only('xs')} {
    padding: 0 16px 21px;
    margin-bottom: 0;
    border-bottom: 1px solid ${(props) => props.theme.colors.grayLight4};
  }
`;

const CurrentTrackingStatus = styled.p`
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.grayDark};
  margin-top: 4px;
`;

const ReturnInfoWrapper = styled.div`
  ${up('md')} {
    display: flex;
    padding-left: 16px;
  }
`;

const ReturnInfo = styled.div<{ featured?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;

  ${up('xs')} {
    padding: 12px;
    background-color: ${(props) =>
      props.featured ? props.theme.colors.grayLight : ''};
  }

  ${up('sm')} {
    padding: 12px;
    width: auto;
    min-width: 170px;
    max-width: 300px;
  }
`;

const ReturnInfoTitle = styled.p`
  color: ${(props) => props.theme.colors.grayDark};
  margin-bottom: 4px;
`;

const ReturnItemsTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  margin: 24px 0 10px;
  padding-left: 16px;
  color: ${(props) => props.theme.colors.darkenBlack2};

  ${only('xs')} {
    padding: 0 12px;
  }
`;

const ReturnItemsWrapper = styled.div`
  display: flex;
  overflow: hidden;
  overflow-x: auto;
  padding-left: 16px;

  ${only('xs')} {
    padding-left: 12px;
    margin-bottom: 24px;
  }
`;

const ReturnItem = styled.div`
  margin-right: 16px;
  width: 160px;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;

  ${up('sm')} {
    width: 144px;
  }
`;

const ReturnItemImageWrapper = styled.div`
  width: 160px;
  height: 202px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 4px;

  ${up('md')} {
    width: 144px;
    height: 160px;
  }

  img {
    height: 100%;
    max-height: 100%;
    z-index: -10;
  }
`;

const PlaceHolderWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.grayDark4};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  svg {
    fill: ${(props) => props.theme.colors.white};
    margin-bottom: 6px;
    width: 56px;
    height: 56px;
  }
`;

const PlaceholderInfo = styled.p`
  color: ${(props) => props.theme.colors.grayDark};
`;

const ItemDescription = styled.p`
  max-width: 100%;
  color: ${(props) => props.theme.colors.grayDark5};
  margin-bottom: 8px;
`;

const ItemInfo = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
`;

const ReturnTrackingHistoryWrapper = styled.div<{ bordered: boolean }>`
  display: none;
  overflow-x: auto;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.1),
    0 1px 2px rgba(0, 0, 0, 0.06);
  margin-top: 24px;

  ${up('sm')} {
    display: block;
    border-bottom-left-radius: ${({ bordered }) => (bordered ? '12px' : 0)};
    border-bottom-right-radius: ${({ bordered }) => (bordered ? '12px' : 0)};
  }
`;

const TrackingRow = styled.div<{ featured: boolean; bordered?: boolean }>`
  width: 100%;
  min-width: 800px;
  display: flex;

  color: ${(props) => props.theme.colors.grayDark};
  background-color: ${(props) =>
    props.featured ? props.theme.colors.grayLight : props.theme.colors.white};

  ${(props) =>
    props.bordered
      ? `
    border-top: 1px solid ${props.theme.colors.grayLight4};
    border-bottom: 1px solid ${props.theme.colors.grayLight4};`
      : ''}
`;

const StatusColumnTitle = styled.p`
  padding: 16px 0 16px 24px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  width: 30%;
`;

const LocationColumnTitle = styled(StatusColumnTitle)`
  width: 35%;
`;

const DateColumnTitle = styled(StatusColumnTitle)`
  width: auto;
`;

const StatusColumn = styled.p`
  padding: 16px 0 16px 24px;
  width: 30%;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.grayDark4};
`;

const LocationColumn = styled.p`
  padding: 16px 0 16px 24px;
  width: 35%;
  font-size: 14px;
  line-height: 20px;
`;

const DateColumn = styled.p`
  padding: 16px 0 16px 24px;
  width: auto;
  font-size: 14px;
  line-height: 20px;
`;

const ReturnTrackingHistoryWrapperMobile = styled.div`
  display: block;
  padding: 0 12px;

  ${up('md')} {
    display: none;
  }
`;

const TrackingHistoryListMobile = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const TrackingHistoryTitle = styled.h3`
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.grayDark4};
  margin-bottom: 12px;
`;

const TrackingHistorySubtitleMobile = styled.div<{ scheme: string }>`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ scheme }) => colorSchemeConfig[scheme].v500};
`;

const TrackingHistoryItemStatusMobile = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.grayDark5};
`;

const TrackingHistoryItemContentMobile = styled.div<{ scheme: string }>`
  border-left: 3px solid ${({ scheme }) => colorSchemeConfig[scheme].v200};
  padding: 4px 16px;
  box-sizing: border-box;
`;

const TrackingHistoryItemLocationMobile = styled.div`
  margin-bottom: 8px;
  line-height: 24px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayLight3};
`;
const TrackingHistoryItemDateMobile = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.grayDark4};
`;

const ShowFullHistoryButtonWrapper = styled.div<{
  isVisibleDesktop: boolean;
  isVisibleMobile: boolean;
}>`
  display: ${({ isVisibleMobile }) => (isVisibleMobile ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  margin: 16px 12px 0;

  ${up('md')} {
    margin: 20px 0 24px;
    display: ${({ isVisibleDesktop }) => (isVisibleDesktop ? 'flex' : 'none')};
  }
`;

const ShowFullHistoryButton = styled(SecondaryButton)`
  padding: 10px 0;

  ${only('xs')} {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  ${up('sm')} {
    width: 374px;
    text-align: center;
  }
`;

const ButtonLink = styled(Link)`
  ${between('sm', 'md')} {
    width: 100%;
  }
`;

const ReturnLabelButtonWrapper = styled.div`
  display: flex;
  gap: 5px;

  ${only('xs')} {
    flex-direction: column;
    width: auto;
    margin: 16px 12px 20px;

    button {
      width: 100%;
    }
  }

  ${up('sm')} {
    position: absolute;
    top: 20px;
    right: 16px;
  }

  ${between('sm', 'md')} {
    flex-direction: column;
    align-items: end;
    top: 48px;
  }
`;

const OrderActionButton = styled(Button)<{ scheme: string }>`
  ${only('xs')} {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  ${up('sm')} {
    min-width: 170px;
    width: auto;
    text-align: center;
  }
`;

const ViewOrderButton = styled(OrderActionButton)`
  button {
    background-color: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.grayDark2};
    color: ${(props) => props.theme.colors.grayDark3};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    font-weight: 500;
  }
`;

export default ReturnCard;
